class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.cartSubscription = subscribe(PUB_SUB_EVENTS.cartUpdate, event => this.normalizeProtection(event.cartData));
    this.updateNiuNiuHeader();
    const state = this.querySelector('[data-cart-count]')?.dataset;
    if (Number(state?.protectionCount) > 0) {
      this.normalizeProtection({ items: [{ variant_id: state.protectionId }] });
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble, .ns-cart');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  updateNiuNiuHeader() {
    const count = this.querySelector('[data-cart-count]')?.dataset.cartCount;
    const badge = document.querySelector('.ns-cart > span');
    if (badge && count !== undefined) badge.textContent = count;
  }

  async normalizeProtection(cart) {
    this.updateNiuNiuHeader();
    const state = this.querySelector('[data-protection-id]')?.dataset;
    const id = state?.protectionId;
    if (!id || Number(state.protectionCount) === 0 || this.cleaningProtection) return;
    const quantity = state.cartCount === '0' ? 0 : 1;
    if (Number(state.protectionCount) === quantity) return;
    this.cleaningProtection = true;
    try {
      const response = await fetch(routes.cart_update_url, { ...fetchConfig(), body: JSON.stringify({
        updates: { [id]: quantity }, sections: this.getSectionsToRender().map(s => s.id), sections_url: window.location.pathname
      }) });
      const result = await response.json();
      if (!response.ok || result.errors) throw new Error(window.nnUi.remove_cover);
      const wasOpen = this.classList.contains('active');
      this.renderContents(result, wasOpen);
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'nn-protection-cleanup', cartData: result });
    } catch (error) {
      const status = this.querySelector('#CartDrawer-CartErrors');
      if (status) status.textContent = error.message;
    } finally { this.cleaningProtection = false; }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, open = true) {
    if (!parsedState.sections?.['cart-drawer']) { window.location.assign(routes.cart_url); return; }
    const updated = this.getSectionDOM(parsedState.sections['cart-drawer'], 'cart-drawer');
    this.classList.toggle('is-empty', updated.classList.contains('is-empty'));
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.updateNiuNiuHeader();
      this.normalizeProtection();
      if (open) this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  async onCartUpdate() {
    const drawer = this.closest('cart-drawer');
    try {
    const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
    if (!response.ok || !drawer) return;
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    drawer.querySelector('.drawer__inner').replaceWith(html.querySelector('.drawer__inner'));
    drawer.classList.toggle('is-empty', html.querySelector('cart-drawer').classList.contains('is-empty'));
    drawer.updateNiuNiuHeader();
    drawer.normalizeProtection();
    } catch (error) {
      const status = drawer?.querySelector('#CartDrawer-CartErrors');
      if (status) status.textContent = window.cartStrings.error;
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
